import React, { useEffect } from 'react';
import { noop } from 'lodash';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { MadadAgentBannerItem } from 'store/sagas/apiService/types';
import * as Styled from 'screens/MadadPage/cover/styled';
import { SmallText } from 'ds/components/typography';
import { useLocale } from 'locale';
import config from 'config';
import withEvents from 'analytics/withEvents';
import * as actions from 'store/state/router/actions';
import { NavigateToFn, Route, State as RouteState } from 'config/routes';
import { connect } from 'react-redux';
import AnchorMiddleware from 'components/anchor';
import { OfficeAvatar } from 'screens/MadadPage/card/OfficeAvatar';


interface CoverProps {
  agentBanner?: MadadAgentBannerItem;
  isMobile: boolean;
  city: string;
  onBannerClink?: () => void;
  onBannerExpose?: () => void;
  navigateTo?: NavigateToFn;
  route?: RouteState;
  source?: string;
}

const MOBILE_IMG_HEIGHT = 343;
const MOBILE_IMG_WIDTH = 390;
const RATIO = 2;

const DESKTOP_PLACEHOLDER = 'madad/desktop-cover_placeholder_2026.jpg';
const MOBILE_PLACEHOLDER = 'madad/mobile-cover_placeholder_2026.png';
const DESKTOP_BG = 'madad/desktop-cover_bg_2026.png';
const MOBILE_BG = 'madad/mobile-cover_bg_2026.png';

const Cover: React.FC<CoverProps> = (props) => {
  const { agentBanner, isMobile, city, onBannerClink, onBannerExpose, navigateTo, source } = props;
  const constructImageURI = useImageUrlBuilder();
  const { t } = useLocale();
  const { agentName, officeName, city: agentCity } = agentBanner;
  const isPayedMobileBanner = isMobile && agentBanner && agentBanner.imageUrl;
  const isPayedDesktopBanner = !isMobile && agentBanner && agentBanner.imageUrl;
  const urlDesktop = isPayedDesktopBanner ? config.imagesRoot + agentBanner.imageUrl : null;
  const urlMobile = isPayedMobileBanner ? constructImageURI(agentBanner.imageUrl, {
    width: MOBILE_IMG_WIDTH * RATIO,
    height: MOBILE_IMG_HEIGHT * RATIO,
  }) : null;
  const displayCity = agentCity || city;

  useEffect(() => {
    onBannerExpose();
  }, [ agentBanner ]);

  const handleBannerClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onBannerClink();
    navigateTo(Route.OfficePage, { id: agentBanner.url.split('/')[2], source });
  };

  const mobileBannerPlaceholder = constructImageURI(MOBILE_PLACEHOLDER);
  const desktopBannerPlaceholder = constructImageURI(DESKTOP_PLACEHOLDER);

  if (!agentBanner || !agentBanner.imageUrl) {
    return (
      <Styled.DefaultBanner>
        <Styled.BannerBG src={isMobile ? mobileBannerPlaceholder : desktopBannerPlaceholder} />
      </Styled.DefaultBanner>
    );
  }

  const mobileBannerBG = constructImageURI(MOBILE_BG);
  const desktopBannerBG = constructImageURI(DESKTOP_BG);
  const badgeImageUrl = agentBanner.category === 'top_10' ? 'madad/badge-2026-green.png' : 'madad/badge-2026-gold.png';
  const officeLogoUrl = constructImageURI(agentBanner.officeLogoUrl, { height: 128, width: 128 });

  return (
    <Styled.DefaultBanner>
      <Styled.BannerBG src={isMobile ? mobileBannerBG : desktopBannerBG} />
      {isMobile ? <Styled.MobileTextBG /> : null}
      {isPayedDesktopBanner ? (
        <Styled.DesktopBannerWrapper>
          <AnchorMiddleware href={agentBanner.url}>
            {(href) => (
              <a href={href} target="_blank" rel="nofollow" onClick={handleBannerClick}>
                <Styled.DesktopBannerImageWrapper>
                  <Styled.DesktopAgentInfo>
                    <Styled.DesktopInnerAgentInfo>
                      <OfficeAvatar
                        size="small"
                        imageUrl={officeLogoUrl}
                        alt={t('madad.2023.cover.banner.desktop.officeCity', { officeName, displayCity })}
                        noBorder
                      />
                      <div>
                        <SmallText>{agentName}</SmallText>
                        <SmallText weight="bold">
                          {t('madad.2023.cover.banner.desktop.officeCity', { officeName, displayCity })}
                        </SmallText>
                      </div>
                    </Styled.DesktopInnerAgentInfo>
                  </Styled.DesktopAgentInfo>
                  <img src={urlDesktop} alt={agentName} />
                </Styled.DesktopBannerImageWrapper>
                <Styled.DesktopBadgeWrapper>
                  <img
                    width={200}
                    height={200}
                    src={constructImageURI(badgeImageUrl, { height: 400, width: 400 })}
                    alt={t('madad.2026.logo.alt')}
                  />
                </Styled.DesktopBadgeWrapper>
              </a>
            )}
          </AnchorMiddleware>
        </Styled.DesktopBannerWrapper>
      ) : null}
      {isPayedMobileBanner ? (
        <Styled.MobileBannerWrapper>
          <AnchorMiddleware href={agentBanner.url}>
            {(href) => (
              <a href={href} target="_blank" rel="nofollow" onClick={onBannerClink}>
                <Styled.MobileBadgeWrapper>
                  <img
                    width={150}
                    height={150}
                    src={constructImageURI(badgeImageUrl, { height: 300, width: 300 })}
                    alt={t('madad.2026.logo.alt')}
                  />
                </Styled.MobileBadgeWrapper>
                <Styled.MobileBannerImageWrapper>
                  <img src={urlMobile} alt={agentName}/>
                  <Styled.MobileAgentInfo>
                    <OfficeAvatar
                      size="small"
                      imageUrl={officeLogoUrl}
                      alt={t('madad.2023.cover.banner.desktop.officeCity', { officeName, displayCity })}
                      noBorder
                    />
                    <div>
                      <SmallText>{agentName}</SmallText>
                      <SmallText weight="bold">
                        {t('madad.2023.cover.banner.desktop.officeCity', { officeName, displayCity })}
                      </SmallText>
                    </div>
                  </Styled.MobileAgentInfo>
                </Styled.MobileBannerImageWrapper>
              </a>
            )}
          </AnchorMiddleware>
        </Styled.MobileBannerWrapper>
      ) : null}
    </Styled.DefaultBanner>
  );
};

Cover.defaultProps = {
  onBannerClink: noop,
  onBannerExpose: noop,
};

const mapDispatchToProps = {
  navigateTo: actions.navigateTo,
};

const CoverConnected = connect(null, mapDispatchToProps)(Cover);

const CoverWithEvents = React.memo(withEvents<CoverProps>((sendEvent, props) => ({
  onBannerClink() {
    if (!props.agentBanner || !props.agentBanner.url) return;

    const { agentName, officeName, url } = props.agentBanner;
    sendEvent('madad_agent_picture_click', 'madad', {
      event: {
        agent_details: {
          name: agentName,
          office_name: officeName,
          office_id: url.replace('/agentsOffice/', ''),
        },
        ...(props.source ? { source: props.source } : null),
      },
    });
  },
  onBannerExpose() {
    if (!props.agentBanner || !props.agentBanner.url) return;

    const { agentName, officeName, url } = props.agentBanner;
    sendEvent('madad_agent_picture_expose', 'madad', {
      event: {
        agent_details: {
          name: agentName,
          office_name: officeName,
          office_id: url.replace('/agentsOffice/', ''),
        },
        ...(props.source ? { source: props.source } : null),
      },
    });
  },
})
)(CoverConnected));

export default CoverWithEvents;
